import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { isTourMode } from 'tour/tourMode';

/**
 * A per-tab record of which URL sits at each index of the browser session history.
 *
 * The browser exposes the history length and lets us jump by a delta, but never tells us what
 * URL a given entry holds. React Router stamps each entry with its position (`history.state.idx`),
 * so recording the location at every index on navigation gives the in-app back button enough to
 * decide *where* a POP would land before performing it (see `useGoBack`). Each entry also carries
 * the identity of the page it shows (see `pageKey`), so entries of one page — its tabs — can be
 * told apart from a move to another page.
 *
 * The trail lives in `sessionStorage` because `history.state.idx` survives a full page reload —
 * the stack of URLs has to survive with it or the indices would point at nothing.
 */

const STORAGE_KEY = 'crespect.history-trail';

export interface HistoryEntry {
    /** pathname + search */
    url: string;
    /** Identity of the page shown, shared by all tabs of a read view. */
    page: string;
    /** What the page calls itself in the breadcrumbs, for a back control that names where it leads. */
    label?: string;
}

/** An index the trail knows nothing about, e.g. after the trail was lost or a write failed. */
export type HistoryTrail = (HistoryEntry | null)[];

function toEntry(value: unknown): HistoryEntry | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }
    const { url, page, label } = value as Record<string, unknown>;
    if (typeof url !== 'string' || typeof page !== 'string') {
        return null;
    }
    return typeof label === 'string' ? { url, page, label } : { url, page };
}

export function currentIndex(): number {
    return window.history.state?.idx ?? 0;
}

function readRawHistoryTrail(): string | null {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function parseHistoryTrail(raw: string | null): HistoryTrail {
    try {
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(toEntry) : [];
    } catch {
        return [];
    }
}

export function readHistoryTrail(): HistoryTrail {
    return parseHistoryTrail(readRawHistoryTrail());
}

const listeners = new Set<() => void>();
let snapshot: { raw: string | null; trail: HistoryTrail } | null = null;

function subscribeHistoryTrail(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

// Parsed again only when the stored text changes, so a render reading the trail gets the same array back until a
// write replaces it.
function historyTrailSnapshot(): HistoryTrail {
    const raw = readRawHistoryTrail();
    if (snapshot?.raw !== raw) {
        snapshot = { raw, trail: parseHistoryTrail(raw) };
    }
    return snapshot.trail;
}

/** The trail, re-rendering the caller on every write: on each navigation and each label a page publishes. */
export function useHistoryTrailSnapshot(): HistoryTrail {
    return useSyncExternalStore(subscribeHistoryTrail, historyTrailSnapshot);
}

function writeHistoryTrail(trail: HistoryTrail): void {
    // The guided tour moves by REPLACE and ends on the entry it started on, so the trail stays true without it.
    if (isTourMode()) return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
    } catch {
        // Storage may be unavailable (privacy mode, quota). A trail that missed a write no longer
        // matches the browser history, and a POP computed from it could land on a form. Drop it:
        // the back button then uses its explicit fallback, and later writes pad the missing
        // indices with `null`, which the walk treats as unknown.
        try {
            window.sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            // Nothing more can be done without storage.
        }
    }
    listeners.forEach((listener) => listener());
}

/**
 * Records the current location at its history index.
 *
 * Only a PUSH discards the browser's forward entries, so only a PUSH cuts the trail at that
 * index. A REPLACE swaps the current entry and keeps the forward ones (list pages REPLACE on
 * mount to normalise their query string, right after a POP that may have forward history); a
 * POP only moves the cursor.
 */
export function recordHistoryEntry(idx: number, entry: HistoryEntry, navigationType: 'POP' | 'PUSH' | 'REPLACE'): void {
    const trail = readHistoryTrail();
    if (navigationType === 'PUSH') {
        trail.length = Math.min(trail.length, idx);
    }
    // Fill any gap (e.g. trail lost while the session history kept growing) with unknown markers
    // so the array index keeps matching the history index.
    while (trail.length < idx) {
        trail.push(null);
    }
    // A POP back onto an entry records it again; its label still names the same page.
    const previous = trail[idx];
    trail[idx] = previous?.url === entry.url && previous.label ? { ...entry, label: previous.label } : entry;
    writeHistoryTrail(trail);
}

/**
 * Names the entry at `idx` by its breadcrumb. Skipped unless that entry is already recorded for `url`, so a label
 * published for a page the user has just left never lands on the entry of the page they went to.
 */
export function labelHistoryEntry(idx: number, url: string, label: string): void {
    const trail = readHistoryTrail();
    const entry = trail[idx];
    if (!entry || entry.url !== url || entry.label === label) {
        return;
    }
    trail[idx] = { ...entry, label };
    writeHistoryTrail(trail);
}

/**
 * Mount once, above the routes, so every navigation is recorded.
 *
 * @param resolvePage Maps a URL to the identity of the page it shows (see `pageKey`). Passed in
 * rather than imported because the route table imports the pages, and the pages import the back
 * button that reads this trail.
 */
export function useHistoryTrail(resolvePage: (url: string) => string): void {
    const { pathname, search, key } = useLocation();
    const navigationType = useNavigationType();

    // Keyed on `location.key`: it changes on every navigation, including a PUSH of the URL that is
    // already shown, whereas pathname+search would not.
    useEffect(() => {
        const url = `${pathname}${search}`;
        recordHistoryEntry(currentIndex(), { url, page: resolvePage(url) }, navigationType);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run per navigation, see above
    }, [key]);
}
